import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/useModules";
import { AppTopBar } from "@/components/AppTopBar";
import { SkuCombobox } from "@/components/SkuCombobox";
import { importIdentity } from "@/pages/ImportHistory";

export default function ImportDetail() {
  const { batchId } = useParams();
  const { user } = useAuth();
  const { can: canModule, ready: modReady } = useModules();
  const [prog, setProg] = useState(null);
  const [picking, setPicking] = useState(null);
  const [cand, setCand] = useState(null);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [skuPick, setSkuPick] = useState({});
  const [pendingSkuMap, setPendingSkuMap] = useState(null);
  const [storePick, setStorePick] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [pend, setPend] = useState(null);
  const [pendQ, setPendQ] = useState("");
  const [pendOffset, setPendOffset] = useState(0);

  const canMap = user && (user.role === "OWNER" || user.role === "ADMIN_INVENTORY");

  const load = useCallback(() => {
    const core = [
      api.get(`/orders/imports/${batchId}/progress`),
      api.get(`/orders/imports/${batchId}/picking`),
      api.get(`/mappings/candidates/${batchId}`),
    ];
    Promise.all(core).then(([p, k, c]) => {
      setProg(p.data); setPicking(k.data); setCand(c.data);
    }).catch((err) => { console.error(err); setError(formatApiErrorDetail(err.response?.data?.detail)); });
    // Master/mapping data only loads for users who can map (OWNER/ADMIN). Checker never needs it.
    if (canMap) {
      api.get(`/products`).then((pr) => setProducts(pr.data.filter((x) => x.status === "ACTIVE"))).catch(() => {});
      api.get(`/stores`).then((st) => setStores(st.data.filter((x) => x.status === "ACTIVE"))).catch(() => {});
    }
  }, [batchId, canMap]);
  useEffect(() => { load(); }, [load]);

  const loadPending = useCallback(() => {
    api.get(`/orders/imports/${batchId}/pending-checker`, { params: { q: pendQ, offset: pendOffset, limit: 50 } })
      .then(({ data }) => setPend(data)).catch(() => {});
  }, [batchId, pendQ, pendOffset]);
  useEffect(() => { loadPending(); }, [loadPending]);

  const copyResi = (resi) => {
    if (!resi) return;
    navigator.clipboard?.writeText(resi);
    setNotice(`Resi ${resi} disalin.`);
  };

  const mapSku = async (msku) => {
    setError(""); setNotice("");
    const pid = skuPick[msku];
    if (!pid) { setError("Pilih Master SKU dulu."); return; }
    const product = products.find((p) => p.id === pid);
    // Guardrail: if source SKU differs from the chosen Master SKU code, require explicit confirmation.
    const norm = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (product && norm(msku) !== norm(product.sku_code)) {
      setPendingSkuMap({ msku, product });
      return;
    }
    await doMapSku(msku, pid);
  };

  const doMapSku = async (msku, pid) => {
    setPendingSkuMap(null);
    try {
      const { data } = await api.post("/mappings/sku", { marketplace_sku: msku, product_id: pid });
      setNotice(`SKU "${msku}" dipetakan. ${data.resolved_items} item order terselesaikan.`); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const mapStore = async (sname) => {
    setError(""); setNotice("");
    const sid = storePick[sname];
    if (!sid) { setError("Pilih Toko dulu."); return; }
    try {
      const { data } = await api.post("/mappings/store", { store_name_source: sname, store_id: sid });
      setNotice(`Toko "${sname}" dipetakan. ${data.resolved_orders} order terselesaikan.`); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const closeBatch = async () => {
    setError(""); setNotice("");
    try {
      await api.post(`/orders/imports/${batchId}/close`);
      setNotice("Import Batch ditutup (CLOSED)."); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const printFinal = () => {
    if (!picking?.can_print_final) return;
    window.print();
  };

  if (modReady && !canModule("ORDER_GINEE")) {
    return (<div className="app-shell"><AppTopBar active="orders" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  const hasUnmapped = cand && (cand.unmapped_skus.length > 0 || cand.unresolved_stores.length > 0);
  const canPrint = picking?.can_print_final;

  return (
    <div className="app-shell">
      <AppTopBar active="orders" />
      <main className="content">
        <Link to="/imports" className="back-link no-print" data-testid="back-to-import">← Kembali</Link>
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}
        {notice && <div className="alert-ok" data-testid="notice-banner">{notice}</div>}

        {prog && (
          <section className="panel no-print" data-testid="import-progress-panel">
            <div className="panel-head">
              <h2 className="panel-title" data-testid="import-identity">Import: {importIdentity(prog.imported_at, prog.imported_by_name)}</h2>
              <span className={`status-pill ${prog.status === "CLOSED" ? "off" : "on"}`} data-testid="import-status">{prog.status}</span>
            </div>
            <div className="biz-sub" data-testid="source-file" style={{ marginBottom: 10 }}>Source File: {prog.original_filename}</div>
            <div className="meta-grid">
              <div><span className="meta-label">Total Order</span><span className="total-value" data-testid="prog-total">{prog.total_orders}</span></div>
              <div><span className="meta-label">Sudah Checker</span><span className="total-value" data-testid="prog-processed">{prog.processed}</span></div>
              <div><span className="meta-label">Belum Checker</span><span className="total-value" data-testid="prog-pending">{prog.pending}</span></div>
              <div><span className="meta-label">Blocked</span><span className="total-value" data-testid="prog-blocked">{prog.blocked}</span></div>
            </div>
            <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button className="btn-primary" onClick={printFinal} disabled={!canPrint} data-testid="print-picking-button">Cetak Picking List Final (A4)</button>
              {prog.status === "ACTIVE" && (
                <button className="btn-ghost" onClick={closeBatch} disabled={!prog.all_checked} data-testid="close-batch-button" title={!prog.all_checked ? "Semua order harus selesai Checker dulu" : ""}>Tutup Batch</button>
              )}
            </div>
            {!canPrint && <div className="alert-error" style={{ marginTop: 10 }} data-testid="print-blocked-reason">Picking List final belum bisa dicetak: masih ada {picking?.unmapped_sku_count} SKU yang belum dipetakan ke Master SKU. Selesaikan mapping SKU dulu (mapping toko tidak menghalangi picking).</div>}
          </section>
        )}

        {prog && (
          <section className="panel no-print" data-testid="pending-checker-panel">
            <div className="panel-head">
              <h2 className="panel-title" data-testid="pending-checker-title">Belum Checker {pend ? `(${pend.pending_total})` : ""}</h2>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
              <input className="field-input" style={{ maxWidth: 320 }} placeholder="Cari Nomor Resi / Order ID"
                value={pendQ} onChange={(e) => { setPendOffset(0); setPendQ(e.target.value); }}
                data-testid="pending-search" />
            </div>
            <div className="table-scroll">
              <table className="data-table" data-testid="pending-checker-table">
                <thead><tr><th>Nomor Resi</th><th>Order ID</th><th>Toko</th><th>Status</th><th>Aksi</th></tr></thead>
                <tbody>
                  {(!pend || pend.items.length === 0) && <tr><td colSpan={5} className="empty-cell" data-testid="pending-empty">Tidak ada order yang belum Checker.</td></tr>}
                  {pend && pend.items.map((r) => (
                    <tr key={r.order_id} data-testid={`pending-row-${r.order_id}`}>
                      <td className="mono" data-testid={`pending-resi-${r.order_id}`}>{r.tracking_no || "-"}</td>
                      <td className="mono">{r.order_id}</td>
                      <td>{r.store_name || "-"}</td>
                      <td><span className="status-pill off" data-testid={`pending-status-${r.order_id}`}>BELUM CHECKER</span></td>
                      <td><button className="btn-mini" disabled={!r.tracking_no} onClick={() => copyResi(r.tracking_no)} data-testid={`pending-copy-${r.order_id}`}>Copy Resi</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pend && pend.pending_total > pend.limit && (
              <div className="form-actions">
                <button className="btn-ghost" disabled={pend.offset <= 0} onClick={() => setPendOffset(Math.max(0, pendOffset - 50))} data-testid="pending-prev">Sebelumnya</button>
                <button className="btn-ghost" disabled={pend.offset + pend.items.length >= pend.pending_total} onClick={() => setPendOffset(pendOffset + 50)} data-testid="pending-next">Berikutnya</button>
              </div>
            )}
          </section>
        )}

        {pendingSkuMap && (
          <div className="alert-error no-print" data-testid="confirm-sku-map-dialog" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <span>
              Anda akan memetakan SKU Source <b>{pendingSkuMap.msku}</b> ke Master SKU <b>{pendingSkuMap.product.sku_code}</b>. Pastikan mapping ini benar karena akan dipakai Picking dan Checker.
            </span>
            <span style={{ fontSize: "0.8rem", opacity: 0.85 }}>Perubahan mapping dapat mempengaruhi import berikutnya. Histori transaksi lama tidak diubah.</span>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="btn-primary inline" onClick={() => doMapSku(pendingSkuMap.msku, pendingSkuMap.product.id)} data-testid="confirm-sku-map-yes">Ya, Petakan</button>
              <button className="btn-ghost inline" onClick={() => setPendingSkuMap(null)} data-testid="confirm-sku-map-cancel">Batal</button>
            </div>
          </div>
        )}

        {!canMap && cand && (cand.unmapped_skus.length > 0 || cand.unresolved_stores.length > 0) && (
          <section className="panel no-print" data-testid="batch-not-ready">
            <div className="alert-error">Batch belum siap Picking. Mapping SKU/Toko harus diselesaikan oleh Admin Inventory.</div>
          </section>
        )}

        {canMap && cand && (
          <section className="panel no-print" data-testid="mapping-panel">
            <h2 className="panel-title">Mapping Belum Terpetakan</h2>
            {!hasUnmapped && <div className="empty-cell" data-testid="mapping-empty">Semua SKU & Toko sudah terpetakan.</div>}

            {cand.unmapped_skus.length > 0 && (
              <>
                <h3 className="field-label" style={{ marginTop: 8 }}>SKU Belum Dipetakan (wajib untuk Picking)</h3>
                <div className="table-scroll">
                  <table className="data-table" data-testid="unmapped-sku-table">
                    <thead><tr><th>SKU Source</th><th>Total Qty</th><th>Pilih Master SKU</th><th>Aksi</th></tr></thead>
                    <tbody>
                      {cand.unmapped_skus.map((s, i) => (
                        <tr key={s.marketplace_sku} data-testid={`unmapped-sku-row-${s.marketplace_sku}`}>
                          <td className="mono">{s.marketplace_sku}</td>
                          <td className="mono">{s.total_qty}</td>
                          <td style={{ minWidth: 260 }}>
                            <SkuCombobox products={products} value={skuPick[s.marketplace_sku] || null}
                              onChange={(pid) => setSkuPick((p) => ({ ...p, [s.marketplace_sku]: pid }))} index={i} />
                          </td>
                          <td><button className="btn-mini" onClick={() => mapSku(s.marketplace_sku)} data-testid={`map-sku-button-${s.marketplace_sku}`}>Petakan</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {cand.unresolved_stores.length > 0 && (
              <>
                <h3 className="field-label" style={{ marginTop: 16 }}>Toko Belum Dipetakan (wajib untuk Checker VALID, tidak menghalangi Picking)</h3>
                <div className="table-scroll">
                  <table className="data-table" data-testid="unresolved-store-table">
                    <thead><tr><th>Nama Toko Source</th><th>Jumlah Order</th><th>Pilih Toko</th><th>Aksi</th></tr></thead>
                    <tbody>
                      {cand.unresolved_stores.map((s) => (
                        <tr key={s.store_name_source} data-testid={`unresolved-store-row-${s.store_name_source}`}>
                          <td>{s.store_name_source}</td>
                          <td className="mono">{s.order_count}</td>
                          <td style={{ minWidth: 220 }}>
                            <select className="field-input" value={storePick[s.store_name_source] || ""}
                              onChange={(e) => setStorePick((p) => ({ ...p, [s.store_name_source]: e.target.value }))}
                              data-testid={`store-select-${s.store_name_source}`}>
                              <option value="">-- Pilih Toko --</option>
                              {stores.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                            </select>
                          </td>
                          <td><button className="btn-mini" onClick={() => mapStore(s.store_name_source)} data-testid={`map-store-button-${s.store_name_source}`}>Petakan</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}

        {picking && prog && (
          <section className="panel print-area" data-testid="picking-panel">
            <div className="print-header">
              <h1 className="picking-title">PICKING LIST</h1>
              <div className="picking-meta">
                <div>Import: {importIdentity(prog.imported_at, prog.imported_by_name)}</div>
                <div>Total Order: {picking.total_orders}</div>
              </div>
              {picking.has_conflict && <div className="alert-error" data-testid="picking-conflict-warning">⚠ Ada order dengan import conflict — periksa sebelum picking.</div>}
            </div>
            <table className="data-table picking-table" data-testid="picking-table">
              <thead>
                <tr><th>NO</th><th>SKU</th><th>TOTAL QTY</th><th>BREAKDOWN PACK</th></tr>
              </thead>
              <tbody>
                {picking.lines.length === 0 && <tr><td colSpan={4} className="empty-cell">Tidak ada kebutuhan.</td></tr>}
                {picking.lines.map((l, i) => (
                  <tr key={l.marketplace_sku} data-testid={`picking-row-${l.marketplace_sku}`}>
                    <td>{i + 1}</td>
                    <td className="mono">{l.sku}</td>
                    <td className="mono" data-testid={`picking-qty-${l.marketplace_sku}`}>{l.total_qty} PCS</td>
                    <td>{l.pack_breakdown || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </main>
    </div>
  );
}
